import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Performance-Driven Paid Marketing at Scale",
  seoDescription: "Paid marketing that drives profitable growth. Meta, Google, and LinkedIn advertising with clear attribution and continuous optimization.",
  canonicalUrl: "https://xperfomax.com/paid-marketing",
  category: "services",
  categoryLabel: "Services / Paid Marketing",
  headline: "Performance-Driven Paid Marketing at Scale",
  subheadline: "We build paid marketing systems that drive profitable customer acquisition through data-driven strategy, creative testing, and continuous optimization.",
  ctaText: "Start a serious conversation",
  problemTitle: "Why Paid Ads Burn Budgets",
  problems: [
    "Rising acquisition costs erode margins faster than revenue grows.",
    "Channel performance looks strong, but profit impact remains unclear.",
    "Scaling spend exposes attribution and measurement gaps.",
    "Creative fatigue accelerates faster than teams can iterate.",
  ],
  platformEcosystems: {
    title: "Platform-Native Performance Ecosystems",
    intro: [
      "Some growth constraints exist inside closed platforms where demand already exists.",
      "Food delivery apps, marketplaces, and local discovery platforms control visibility, ranking, and attribution. Performance in these environments is governed by platform logic. Not traditional media buying.",
    ],
    platformMention: "Including platform-native advertising across DoorDash, Uber Eats, Zomato, Swiggy, Amazon, Flipkart, and Google Maps—where competition happens inside the platform, not on the open web.",
    problems: [
      "Visibility is auctioned, but ranking logic remains opaque.",
      "Promotions often cannibalize margin instead of creating incremental demand.",
      "Platform dashboards show activity, not decision-grade insight.",
      "Platform spend is frequently disconnected from broader growth systems.",
    ],
    reassurance: "Often engaged to diagnose a single platform constraint before expanding across channels.",
  },
  systemTitle: "Our Paid Media System",
  systemDescription: "Audience and intent mapping, funnel-aligned creatives, and continuous performance optimization.",
  services: [
    { name: "Meta Ads (Facebook & Instagram)", icon: "Users", components: ["Audience targeting & lookalikes", "Creative testing frameworks", "Conversion optimization"], outcome: "Profitable customer acquisition at scale." },
    { name: "Google Ads (Search, Display, YouTube)", icon: "Target", components: ["Intent-based search campaigns", "Display remarketing", "YouTube video campaigns"], outcome: "Capture demand at the moment of highest intent." },
    { name: "LinkedIn Ads (B2B)", icon: "Zap", components: ["Account-based targeting", "Thought leadership promotion", "Lead generation campaigns"], outcome: "Reach decision-makers in your target accounts." },
    { name: "Funnel & Landing Optimization", icon: "MousePointerClick", components: ["Landing page optimization", "Offer & messaging testing", "Conversion rate optimization"], outcome: "Maximize the value of every click." },
    { name: "Budget & ROAS Tracking", icon: "BarChart3", components: ["Cross-channel attribution", "Budget allocation optimization", "Real-time performance dashboards"], outcome: "Clear visibility into what's driving ROI." },
    { name: "Creative Strategy", icon: "TrendingUp", components: ["Creative concept development", "Ad copy testing", "Visual performance analysis"], outcome: "Creative that performs, not just looks good." },
  ],
  systemShift: {
    title: "The paid media shift.",
    beforeTitle: "Before: Spend & Hope",
    afterTitle: "After: Engineered Acquisition",
    items: [
      { label: "Signal Quality", before: "Broad targeting, hoping algorithms figure it out", after: "Intent-mapped audiences with precision signals" },
      { label: "Attribution", before: "Platform metrics only, unclear true ROI", after: "Cross-channel attribution with revenue clarity" },
      { label: "Creative Strategy", before: "Launch and pray, slow iteration cycles", after: "Systematic creative testing with data-driven iteration" },
      { label: "Budget Allocation", before: "Fixed budgets regardless of performance", after: "Dynamic allocation based on marginal ROAS" },
      { label: "Feedback Loops", before: "Campaigns run in isolation", after: "Integrated loops between channels and CRM" },
    ],
  },
  processTitle: "From strategy to scale.",
  processSteps: [
    { number: "01", title: "Audit & Strategy", description: "Analyze current performance, competitive landscape, and define audience and funnel strategy." },
    { number: "02", title: "Campaign Build", description: "Structure campaigns, build audiences, create tracking, and develop initial creative." },
    { number: "03", title: "Launch & Test", description: "Launch campaigns with structured tests for audience, creative, and offer optimization." },
    { number: "04", title: "Optimize & Scale", description: "Analyze results, double down on winners, and scale profitable campaigns." },
  ],
  timelinePhases: [
    { number: "01", title: "Discovery & Strategy", description: "Deep-dive into your business, audience, and competitive landscape.", decision: "Which channels and audiences to prioritize for maximum impact.", output: "Channel strategy document with audience mapping and budget allocation.", riskRemoved: "Wasting budget on wrong channels or audiences." },
    { number: "02", title: "Infrastructure Setup", description: "Build tracking, attribution, and campaign architecture.", decision: "How to structure campaigns for clear performance signals.", output: "Complete tracking setup, conversion API, and organized campaign structure.", riskRemoved: "Flying blind without accurate attribution data." },
    { number: "03", title: "Creative Development", description: "Develop performance creative based on proven frameworks.", decision: "Which messaging angles and formats to test first.", output: "Initial creative suite with multiple variants for systematic testing.", riskRemoved: "Launching with unproven creative that burns budget." },
    { number: "04", title: "Launch & Learning", description: "Controlled launch with structured testing protocols.", decision: "Which variables to test and what thresholds define success.", output: "Live campaigns with clear testing framework and reporting cadence.", riskRemoved: "Premature scaling before identifying what works." },
    { number: "05", title: "Optimize & Scale", description: "Continuous optimization based on performance data.", decision: "Where to allocate marginal budget for maximum ROAS.", output: "Scaled campaigns with documented playbooks and ongoing optimization.", riskRemoved: "Plateauing performance without clear path to scale." },
  ],
  technologyCategories: [
    { title: "Advertising", icon: "Megaphone", items: ["Meta Ads Manager", "Google Ads", "LinkedIn Campaign Manager"] },
    { title: "Analytics", icon: "LineChart", items: ["GA4", "Triple Whale", "Northbeam"] },
    { title: "Creative", icon: "Palette", items: ["Figma", "Canva", "Adobe Creative Cloud"] },
    { title: "Tracking", icon: "Tag", items: ["Google Tag Manager", "Meta Pixel", "Conversion API"] },
  ],
  techTitle: "Paid Media Platforms & Tools",
  techDescription: "Certified and experienced across major advertising platforms.",
  qualification: {
    forYou: [
      "You have a validated offer and are ready to scale",
      "You're willing to invest in testing to find what works",
      "You understand that paid marketing is an investment in learning",
      "You have margins that support customer acquisition costs",
    ],
    notForYou: [
      "You expect immediate profitability without testing",
      "You don't have a clear offer or landing page",
      "You're not willing to iterate on creative and messaging",
      "You want to set it and forget it",
    ],
  },
  faqs: [
    { question: "How much should I spend on paid marketing?", answer: "It depends on your goals, margins, and current performance. We recommend starting with a test budget to establish baselines, then scaling based on proven ROAS." },
    { question: "How long until I see results?", answer: "You'll see data within days. Statistically significant learnings typically take 2-4 weeks. Profitable scale usually takes 2-3 months of optimization." },
    { question: "What's your approach to creative?", answer: "We treat creative as a performance lever. We develop concepts based on what's working, test multiple variations, and iterate based on data. Not opinions." },
    { question: "How do you handle attribution challenges?", answer: "We use a combination of platform data, server-side tracking, and third-party attribution tools. We focus on directional trends rather than perfect last-click attribution." },
  ],
  finalCtaTitle: "Scale without waste.",
  finalCtaDescription: "Profitable paid marketing is possible. Let's discuss how to build your growth engine.",
  finalCtaButtonText: "Start a serious conversation",
};

const PaidMarketing = () => {
  const { content } = usePageContent("paid-marketing", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories?.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  // CMS-driven block ordering (stored as content._blocks)
  const blocks = (content as any)._blocks || undefined;

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      platformEcosystems={content.platformEcosystems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      systemShift={content.systemShift}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      timelinePhases={content.timelinePhases}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
      blocks={blocks}
    />
  );
};

export default PaidMarketing;
